/**
 * مسارات المهام والرسائل الجاهزة — اللي شاشة «مكتبي» بتاكل منها.
 *
 * الأشكال هنا مطابقة لـ`backend/app/routers/tasks.py::serialize_task`
 * و`backend/app/routers/outbound.py::public_draft` — أي حقل مش موجود هناك
 * ما بيتخترعش هنا.
 *
 * ⚠️ قاعدتان مقفولتان بيتنفّذوا في الملف ده:
 *   1) **ممنوع «نجاح» على رد فاضي** — رد 200 بجسم `null` مش نجاح، بيتسجّل
 *      ERROR وبيترمي زي أي فشل. (العطل الحقيقي: `/auth/me` رد `null`
 *      واتفسّر «داخل بلا هوية».)
 *   2) النصوص المعروضة للمستخدم بتيجي من السيرفر (`message_ar` ·
 *      `sla_label_ar` · `primary_action.label_ar` · `source_label_ar`) —
 *      الواجهة ما بتصيغش نص بديل عنها.
 */
import { ApiError, api } from "./client";
import type { Envelope, ListMeta } from "./types";

/* ─────────────────────────── الأنواع ─────────────────────────── */

export type TaskState = "open" | "claimed" | "blocked" | "done" | "cancelled";

/** ⚠️ `telegram` مصدر حقيقي في الباك إند — لازم يتحسب وإلا مجموع الفلاتر
 *  ما يساويش «الكل»، وده أخطر بلاغ في التدقيق. */
export type TaskSource = "journey" | "event" | "assigned" | "self" | "telegram";

export type TaskActionKey = "claim" | "nudge" | "complete";

/** الأولويتين المعتمدتين في المحرّك — ⛔ ممنوع قيمة تالتة مخترعة */
export type TaskPriority = "normal" | "urgent";

export const PRIORITY_OPTIONS: { key: TaskPriority; label: string }[] = [
  { key: "normal", label: "عادية" },
  { key: "urgent", label: "عاجلة" },
];

/** مهل جاهزة لصندوق التوزيع — الرقم بيتحوّل لـ`due_at` في السيستم */
export const SLA_OPTIONS: { hours: number | null; label: string }[] = [
  { hours: null, label: "بلا مهلة" },
  { hours: 4, label: "خلال 4 ساعات" },
  { hours: 24, label: "خلال يوم" },
  { hours: 72, label: "خلال 3 أيام" },
];

export type TaskChecklistItem = {
  key: string;
  label_ar: string;
  required: boolean;
  done: boolean;
};

export type TaskBlockedBy = {
  waiting_on_label_ar: string | null;
  waiting_on_dept: string | null;
  since: string | null;
  waiting_days: number | null;
  expected_event: string | null;
  nudge_count: number;
  /** فيه مهلة تبريد بين تذكير وتذكير — الزر بيتقفل لما تكون false */
  can_nudge: boolean;
};

export type TaskJourneyRef = {
  id: string;
  step_no: number | null;
  of: number;
  step_title_ar: string;
};

export type TaskEntity = {
  type: string | null;
  id: string | null;
  label_ar: string;
};

export type DeskTask = {
  id: string;
  title_ar: string;
  body_ar: string;
  source: TaskSource;
  source_label_ar: string;
  state: TaskState;
  dept: string | null;
  assignee_id: string | null;
  claimed_at: string | null;
  claim_note_ar: string | null;
  entity: TaskEntity;
  journey: TaskJourneyRef | null;
  priority: string;
  due_at: string | null;
  is_overdue: boolean;
  /** «متأخرة 3 ساعات» · «باقي ساعتين» — بيتولّد في الباك إند */
  sla_label_ar: string | null;
  blocked_by: TaskBlockedBy | null;
  checklist: TaskChecklistItem[];
  close_note_ar: string | null;
  undo_expires_at: string | null;
  primary_action: { key: TaskActionKey; label_ar: string } | null;
  version: number;
};

/**
 * عنصر في طابور القسم — `GET /tasks/queue`.
 * الحقلين الزيادة **بيتولّدوا في الباك إند** (`routers/tasks.py::dept_queue`)
 * زيّهم زيّ `sla_label_ar` — الواجهة ما بتصيغش نص انتظار من عندها.
 */
export type QueueTask = DeskTask & {
  /** «في الطابور من 25 دقيقة» */
  waiting_label_ar: string;
  /** عدّى مهلة الاستلام ⇒ حارس السقوط هيسنده لأقل حِمل */
  will_be_assigned: boolean;
};

/** عدّادات `with_counts=true` — كلها على نفس الكون: مهامي المفتوحة */
export type TaskCounts = {
  all: number;
  overdue: number;
  blocked_by_others: number;
} & Partial<Record<TaskSource, number>>;

export type TaskPage = {
  tasks: DeskTask[];
  meta: ListMeta | null;
  counts: TaskCounts | null;
};

export type Handoff = {
  id: string;
  title_ar: string | null;
  body_ar: string | null;
  origin_dept: string | null;
  /** «من 20 دقيقة» — بيتولّد في الباك إند */
  since_label_ar: string | null;
  /**
   * ⚠️ مفتاح المهمة في الوجهة اسمه **`task`** — ده اللي
   * `services/tasks.py::task_link` بيكتبه. `id` كان الاسم القديم وفيه صفوف
   * قديمة في القاعدة لسه بيه، فبنقبل الاتنين في القراءة بس.
   */
  deep_link: { screen?: string; params?: { task?: string; id?: string } } | null;
};

/** معرّف المهمة اللي الإشعار بيوديّ لها — أو `null` لو الوجهة مش مهمة */
export function taskIdOfLink(link: Handoff["deep_link"]): string | null {
  const params = link?.params;
  return params?.task ?? params?.id ?? null;
}

export type DraftStatus = "ready" | "sent" | "skipped";

export type OutboundDraft = {
  id: string;
  template_key: string;
  template_name_ar: string;
  /** الخام — ده اللي بيتنسخ ورايح واتساب زي ما هو */
  body_ar: string;
  /** للعرض بس — مهرّب من السيرفر وفيه `<bdi>` للمواعيد */
  body_html: string;
  status: DraftStatus;
  status_label_ar: string;
  sent_via_label_ar: string | null;
  entity: TaskEntity | null;
  version: number;
};

/** قنوات «تم الإرسال» — «جوجل شات» مؤجّل بقرار المؤسس فمش معروض */
export type SentVia = "whatsapp_group" | "whatsapp_direct" | "call";

export const SENT_VIA_OPTIONS: { key: SentVia; label: string }[] = [
  { key: "whatsapp_group", label: "جروب واتساب" },
  { key: "whatsapp_direct", label: "واتساب خاص" },
  { key: "call", label: "مكالمة" },
];

/**
 * أسماء الأقسام بالعربي.
 * ⚠️ منقولة من `backend/app/services/journeys.py::DEPT_LABELS_AR` لأن
 *    `/tasks/handoffs` بيرجّع كود القسم بس. لو الباك إند رجّع
 *    `origin_dept_ar` تتشال من هنا فورًا (مصدر واحد أحسن من نسختين).
 */
const DEPT_LABELS_AR: Record<string, string> = {
  student_support: "دعم الطلاب",
  evaluation: "التقييم والترشيح",
  accounts: "الحسابات",
  admin_supervision: "الإشراف الإداري",
  technical_supervision: "الإشراف الفني",
  hiring: "التوظيف",
  training_recordings: "التدريب والتسجيلات",
  management: "الإدارة",
  system: "تلقائي",
};

/**
 * الأقسام اللي ينفع مهمة تتوزّع عليها — «تلقائي» مش قسم فيه بني آدمين
 * فمستبعد من أي قايمة اختيار.
 */
export const DEPT_OPTIONS: { key: string; label: string }[] = Object.entries(DEPT_LABELS_AR)
  .filter(([key]) => key !== "system")
  .map(([key, label]) => ({ key, label }));

export function deptLabelAr(code: string | null | undefined): string {
  if (!code) return "";
  const label = DEPT_LABELS_AR[code];
  if (label) return label;
  // ⛔ ممنوع الفشل الصامت: قسم جديد في الباك إند بلا اسم هنا = كود إنجليزي
  //    بيظهر لموظف عربي. بنصرخ في اللوج وبنعرض الكود بدل ما نخفي المصدر.
  console.error(`[tasks] قسم مالوش اسم عربي في الواجهة: ${code}`);
  return code;
}

/* ─────────────────────── حرّاس الرد الفاضي ─────────────────────── */

function emptyResponse(path: string, payload: unknown): ApiError {
  console.error(`[api] ${path} رجّع نجاح بلا بيانات`, payload);
  return new ApiError(
    500,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رد بلا بيانات — جرّب تاني" },
    null,
  );
}

/** رد عنصر واحد — `null`/`undefined` **مش** نجاح */
function expectItem<T>(res: Envelope<T>, path: string): T {
  const data = res?.data;
  if (data === null || data === undefined) throw emptyResponse(path, res);
  return data;
}

/** رد قائمة — أي حاجة مش مصفوفة عطل مش «صفر نتايج» */
function expectList<T>(res: Envelope<T[]>, path: string): T[] {
  const data = res?.data;
  if (!Array.isArray(data)) throw emptyResponse(path, res);
  return data;
}

/**
 * ردود الكتابة فيها `toast_ar` فوق الغلاف — الباك إند هو اللي بيكتب نص
 * التأكيد («اتقفلت الخطوة، ودور القسم اللي بعده بدأ»).
 */
type MutationEnvelope<T> = Envelope<T> & {
  toast_ar?: string;
  side_effects?: Record<string, unknown>;
};

export type TaskMutation = { task: DeskTask; toastAr: string | null };

function expectMutation(res: Envelope<DeskTask>, path: string): TaskMutation {
  const task = expectItem(res, path);
  // الحقول الزيادة موجودة في الرد الحقيقي بس مش في نوع الغلاف العام
  const withToast = res as MutationEnvelope<DeskTask>;
  return { task, toastAr: withToast.toast_ar ?? null };
}

/** مفتاح منع التكرار — نفس المفتاح لنفس الضغطة حتى لو اتعادت المحاولة */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `k-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* ─────────────────────────── القراءة ─────────────────────────── */

export type ListTasksOptions = {
  scope?: "me" | "blocked_by_others" | "department";
  /** حالات بفاصلة («done») — مع `scope=me` بتجيب مهامي اللي قفلتها */
  state?: string;
  page?: number;
  perPage?: number;
  signal?: AbortSignal;
  withCounts?: boolean;
};

/** `scope=me` = مهامي المفتوحة والمستلمة — **المقفولة مش داخلة** */
export async function listTasks(opts: ListTasksOptions = {}): Promise<TaskPage> {
  const path = "/tasks";
  const res = await api.get<DeskTask[]>(path, {
    query: {
      scope: opts.scope ?? "me",
      ...(opts.state ? { state: opts.state } : {}),
      page: opts.page ?? 1,
      per_page: opts.perPage ?? 100,
      with_counts: opts.withCounts ? "true" : "false",
    },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  return {
    tasks: expectList(res, path),
    meta: res.meta ?? null,
    counts: (res.counts as TaskCounts | undefined) ?? null,
  };
}

/**
 * 🔑 طابور فريقي — المهام **غير الموزّعة** في قسمي.
 * ده اللي كان ناقص خالص: الموظف ماكانش عنده أي شاشة يشوف فيها شغل فريقه
 * قبل ما يتسند لحد، فـ«محدش فينا عارف مين هيعملها» كانت بتفضل قايمة.
 */
export async function listQueue(
  dept?: string | null,
  signal?: AbortSignal,
): Promise<{ tasks: QueueTask[]; meta: ListMeta | null }> {
  const path = "/tasks/queue";
  const res = await api.get<QueueTask[]>(path, {
    query: { dept: dept ?? undefined, per_page: 50 },
    ...(signal ? { signal } : {}),
  });
  const rows = expectList(res, path);
  for (const row of rows) {
    // ⛔ ممنوع الفشل الصامت: نص الانتظار بيتولّد في السيستم — غيابه عطل
    //    مش «مفيش انتظار»، وبنصرخ بدل ما نخترع نص بديل.
    if (!row.waiting_label_ar) {
      console.error("[tasks] عنصر في الطابور بلا نص انتظار من السيستم", row.id);
    }
  }
  return { tasks: rows, meta: res.meta ?? null };
}

/** المتأخر عندي — مسار مستقل عشان الترتيب والحد بيتعملوا في السيستم */
export async function listOverdue(signal?: AbortSignal): Promise<TaskPage> {
  const path = "/tasks/overdue";
  const res = await api.get<DeskTask[]>(path, {
    query: { scope: "me" },
    ...(signal ? { signal } : {}),
  });
  return { tasks: expectList(res, path), meta: res.meta ?? null, counts: null };
}

/**
 * مين ينفع أسلّمه مهمة أو أوزّع عليه — `GET /tasks/assignable`.
 *
 * ⚠️ مسار مستقل عن `/admin/users` عن قصد: العضو نطاقه على المستخدمين هو
 *    **نفسه بس**، فده المصدر الوحيد لقايمة الوجهات. وبيرجّع الاسم بس —
 *    مفيش بريد ولا تليفون ولا دور.
 */
export type AssignablePerson = { id: string; name_ar: string; dept: string | null };

export async function listAssignable(
  dept?: string | null,
  signal?: AbortSignal,
): Promise<AssignablePerson[]> {
  const path = "/tasks/assignable";
  const res = await api.get<AssignablePerson[]>(path, {
    query: { dept: dept ?? undefined },
    ...(signal ? { signal } : {}),
  });
  return expectList(res, path);
}

/** «تسليمات وصلتني» — إشعارات التسليم غير المقروءة */
export async function listHandoffs(signal?: AbortSignal): Promise<Handoff[]> {
  const path = "/tasks/handoffs";
  const res = await api.get<Handoff[]>(path, { ...(signal ? { signal } : {}) });
  return expectList(res, path);
}

/** الرسائل الجاهزة للإرسال — ⚠️ بتتجهّز بس، السيستم ما بيبعتش لولي الأمر */
export async function listReadyDrafts(
  limit = 5,
  signal?: AbortSignal,
): Promise<OutboundDraft[]> {
  const path = "/outbound/drafts";
  const res = await api.get<OutboundDraft[]>(path, {
    query: { mine: "true", status: "ready", per_page: limit },
    ...(signal ? { signal } : {}),
  });
  return expectList(res, path);
}

/* ─────────────────────────── الكتابة ─────────────────────────── */

/**
 * 🔑 الاستلام — ذرّي في السيستم: اتنين بيدوسوا في نفس اللحظة ⇒ واحد ينجح
 * والتاني بياخد `TASK_ALREADY_CLAIMED` ومعاه اسم اللي استلمها ووقتها.
 */
export async function claimTask(
  taskId: string,
  version: number,
  idempotencyKey: string,
): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/claim`;
  const res = await api.post<DeskTask>(path, undefined, {
    ifMatch: version,
    idempotencyKey,
  });
  return expectMutation(res, path);
}

/** ⛔ الإقفال بدليل — بلا ملاحظة ولا تشيك ليست مكتملة السيستم بيرفض */
export async function completeTask(
  taskId: string,
  closeNoteAr: string,
  version: number,
): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/complete`;
  const res = await api.post<DeskTask>(
    path,
    { close_note_ar: closeNoteAr || null },
    { ifMatch: version },
  );
  return expectMutation(res, path);
}

export async function undoComplete(taskId: string): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/undo`;
  const res = await api.post<DeskTask>(path);
  return expectMutation(res, path);
}

export async function nudgeTask(taskId: string): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/nudge`;
  const res = await api.post<DeskTask>(path);
  return expectMutation(res, path);
}

export async function setChecklistItem(
  taskId: string,
  key: string,
  done: boolean,
): Promise<DeskTask> {
  const path = `/tasks/${taskId}/checklist/${encodeURIComponent(key)}`;
  const res = await api.patch<DeskTask>(path, { done });
  return expectItem(res, path);
}

/**
 * «+ مهمة» و«وزّع مهمة» — نفس المسار.
 *
 * من غير `assigneeId` بتتسجّل باسمي بمصدر «أنا ضفتها» و**مستلَمة** من أول
 * لحظة. معاه بتتسجّل «مسندة ليك» وبتفضل في الطابور لحد ما هو يستلمها —
 * «الاستلام بيثبّت المسؤولية».
 *
 * ⚠️ `assigneeId` بيتطلب `task.create_for_others`، والشخص لازم يكون من
 *    قسمي — والسيستم بيفحص الاتنين، فحتى لو الزرار اتسرّب الطلب بيترفض.
 */
export async function createTask(input: {
  titleAr: string;
  bodyAr?: string;
  assigneeId?: string | null;
  dept?: string | null;
  priority?: TaskPriority;
  slaHours?: number | null;
}): Promise<DeskTask> {
  const path = "/tasks";
  const res = await api.post<DeskTask>(path, {
    title_ar: input.titleAr,
    body_ar: input.bodyAr ?? "",
    ...(input.assigneeId ? { assignee_id: input.assigneeId } : {}),
    ...(input.dept ? { dept: input.dept } : {}),
    ...(input.priority ? { priority: input.priority } : {}),
    ...(input.slaHours ? { sla_hours: input.slaHours } : {}),
  });
  return expectItem(res, path);
}

/**
 * 🔑 «اشتغلت فيها بس ما تمتش» — طلب الفريق الحرفي.
 * الحالة بتفضل `claimed` والمهمة **ما بتتحسبش مقفولة**، والكارت بيعرض
 * «آخر تحديث منك».
 */
export async function noteProgress(
  taskId: string,
  noteAr: string,
  version: number,
): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/progress`;
  const res = await api.post<DeskTask>(path, { claim_note_ar: noteAr }, { ifMatch: version });
  return expectMutation(res, path);
}

/**
 * «أنا مستني حد» — اللافتة العربية **إجبارية**: الكارت لازم يقول مستني
 * مين بالظبط، وده أهم سلوك موثّق في الأكاديمية («دورنا بيوقف وقفات»).
 */
export async function blockTask(
  taskId: string,
  waitingOnLabelAr: string,
  version: number,
): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/block`;
  const res = await api.post<DeskTask>(
    path,
    { waiting_on_label_ar: waitingOnLabelAr },
    { ifMatch: version },
  );
  return expectMutation(res, path);
}

/** «سلّم لحد» — البديل الرسمي للمنشن على الواتساب. السبب إجباري. */
export async function handoffTask(
  taskId: string,
  toUserId: string,
  reasonAr: string,
  version: number,
): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/handoff`;
  const res = await api.post<DeskTask>(
    path,
    { to_user_id: toUserId, reason_ar: reasonAr },
    { ifMatch: version },
  );
  return expectMutation(res, path);
}

/** الإلغاء — حذف ناعم بسبب إجباري، وللمنسق والمدير بس (`task.cancel`) */
export async function cancelTask(
  taskId: string,
  reasonAr: string,
  version: number,
): Promise<TaskMutation> {
  const path = `/tasks/${taskId}/cancel`;
  const res = await api.post<DeskTask>(
    path,
    { cancel_reason_ar: reasonAr },
    { ifMatch: version },
  );
  return expectMutation(res, path);
}

/**
 * **«تم الإرسال»** — توثيق إن إنسان بعت، مش إرسال من السيستم.
 * ⛔ ممنوع أي نص في الواجهة يقول إن السيستم بيبعت لولي الأمر.
 */
export async function markDraftSent(
  draftId: string,
  sentVia: SentVia,
  version: number,
): Promise<OutboundDraft> {
  const path = `/outbound/drafts/${draftId}/sent`;
  const res = await api.post<OutboundDraft>(path, { sent_via: sentVia }, { ifMatch: version });
  return expectItem(res, path);
}
